import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const SuitabilityAssessment = () => {
  const [searchParams]  = useSearchParams();
  const token           = searchParams.get('token');
  const investorId      = searchParams.get('investor_id');

  const [step,         setStep]         = useState('loading'); // loading | chat | finalising | done | error
  const [sessionToken, setSessionToken] = useState(null);
  const [messages,     setMessages]     = useState([]);
  const [input,        setInput]        = useState('');
  const [sending,      setSending]      = useState(false);
  const [result,       setResult]       = useState(null);
  const [fundName,     setFundName]     = useState('');
  const [investorName, setInvestorName] = useState('');
  const [error,        setError]        = useState(null);
  const bottomRef = useRef(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Start session on load
  useEffect(() => {
    if (!token || !investorId) {
      setError('Invalid link. Please use the link from your email.');
      setStep('error');
      return;
    }

    axios.post(`${API_URL}/suitability/public/start`, { investor_id: investorId, token })
      .then(res => {
        setSessionToken(res.data.session_token);
        setFundName(res.data.fund_name || '');
        setInvestorName(res.data.investor_name || '');
        setMessages([{ role: 'assistant', content: res.data.first_message }]);
        setStep('chat');
      })
      .catch(err => {
        setError(err.response?.data?.error || 'Failed to start assessment. Please try again.');
        setStep('error');
      });
  }, [token, investorId]);

  const sendMessage = async () => {
    if (!input.trim() || sending) return;

    const userMessage = input.trim();
    setInput('');
    setSending(true);
    setMessages(m => [...m, { role: 'user', content: userMessage }]);

    try {
      const res = await axios.post(`${API_URL}/suitability/public/respond`, {
        session_token: sessionToken,
        message:       userMessage,
      });

      setMessages(m => [...m, { role: 'assistant', content: res.data.message }]);

      if (res.data.status === 'ASSESSMENT_COMPLETE') {
        setStep('finalising');
        finalise();
      }
    } catch (err) {
      setMessages(m => [...m, {
        role:    'assistant',
        content: '⚠️ Something went wrong. Please try again.',
        error:   true,
      }]);
    } finally {
      setSending(false);
    }
  };

  const finalise = async () => {
    try {
      const res = await axios.post(`${API_URL}/suitability/public/finalise`, {
        session_token: sessionToken,
      });
      setResult(res.data.result);
      setStep('done');
    } catch (err) {
      setError('Failed to finalise assessment. Please contact your fund manager.');
      setStep('error');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ── Loading
  if (step === 'loading') return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
        <p style={{ fontSize: 28, margin: '0 0 12px' }}>⏳</p>
        Starting your suitability assessment…
      </div>
    </div>
  );

  // ── Error
  if (step === 'error') return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{
        background: 'white', borderRadius: 14, padding: '40px 36px',
        maxWidth: 440, textAlign: 'center',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      }}>
        <p style={{ fontSize: 40, margin: '0 0 16px' }}>⚠️</p>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1e3a5f', margin: '0 0 10px' }}>
          Assessment Error
        </h2>
        <p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>{error}</p>
        <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 16 }}>
          Please contact your fund manager for assistance.
        </p>
      </div>
    </div>
  );

  // ── Done
  if (step === 'done') return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', padding: '40px 16px' }}>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: '#1e3a5f', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🛡️</div>
          <span style={{ fontSize: 20, fontWeight: 800, color: '#1e3a5f', letterSpacing: '-0.02em' }}>RegShield</span>
        </div>
      </div>
      <div style={{
        maxWidth: 520, margin: '0 auto', background: 'white',
        borderRadius: 16, padding: '40px', textAlign: 'center',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      }}>
        <div style={{
          width: 80, height: 80, borderRadius: '50%',
          background: result === 'SUITABLE' ? '#dcfce7' : result === 'UNSUITABLE' ? '#fee2e2' : '#fef9c3',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px', fontSize: 36,
        }}>
          {result === 'SUITABLE' ? '✅' : result === 'UNSUITABLE' ? '❌' : '⚠️'}
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1e3a5f', margin: '0 0 10px' }}>
          Assessment Complete
        </h2>
        <div style={{
          display: 'inline-block',
          background: result === 'SUITABLE' ? '#dcfce7' : result === 'UNSUITABLE' ? '#fee2e2' : '#fef9c3',
          color:      result === 'SUITABLE' ? '#166534' : result === 'UNSUITABLE' ? '#991b1b' : '#854d0e',
          padding: '6px 20px', borderRadius: 20,
          fontSize: 14, fontWeight: 700, marginBottom: 16,
        }}>
          {result}
        </div>
        <p style={{ fontSize: 14, color: '#6b7280', maxWidth: 380, margin: '0 auto 8px', lineHeight: 1.6 }}>
          {result === 'SUITABLE'
            ? `Thank you, ${investorName || 'Investor'}. Based on your answers, this investment appears suitable for you. Your compliance officer will review the assessment before proceeding.`
            : result === 'UNSUITABLE'
            ? `Thank you, ${investorName || 'Investor'}. Based on your answers, this investment may not be suitable for your current financial situation. Your compliance officer will contact you.`
            : `Thank you, ${investorName || 'Investor'}. Your assessment requires additional review by our compliance team. They will contact you shortly.`
          }
        </p>
        <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 16 }}>
          This assessment was conducted in compliance with MiFID II Article 25.
          A detailed report will be available from your fund manager.
        </p>
      </div>
      <p style={{ textAlign: 'center', fontSize: 11, color: '#d1d5db', marginTop: 24 }}>
        🔒 Secured by RegShield · GDPR compliant · Data processed in Luxembourg
      </p>
    </div>
  );

  // ── Chat
  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <div style={{
        background: 'white', borderBottom: '1px solid #f0f0f0',
        padding: '14px 24px', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: '#1e3a5f', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🛡️</div>
          <div>
            <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: '#1e3a5f' }}>RegShield</p>
            <p style={{ margin: 0, fontSize: 11, color: '#9ca3af' }}>MiFID II Suitability Assessment</p>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: '#374151' }}>{fundName}</p>
          <p style={{ margin: 0, fontSize: 11, color: '#9ca3af' }}>{investorName}</p>
        </div>
      </div>

      {/* Info banner */}
      <div style={{
        background: '#eff6ff', borderBottom: '1px solid #bfdbfe',
        padding: '10px 24px', fontSize: 12, color: '#1d4ed8', fontWeight: 500,
      }}>
        🔒 This assessment is required by MiFID II regulation. Please answer all questions honestly and accurately.
        Press Enter or click Send to submit each answer.
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px', maxWidth: 720, width: '100%', margin: '0 auto' }}>
        {messages.map((msg, idx) => (
          <div key={idx} style={{
            display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            marginBottom: 16,
          }}>
            {msg.role === 'assistant' && (
              <div style={{
                width: 32, height: 32, borderRadius: '50%', background: '#1e3a5f',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, marginRight: 10, flexShrink: 0, marginTop: 4,
              }}>🛡️</div>
            )}
            <div style={{
              maxWidth: '75%',
              background:   msg.role === 'user' ? '#1e3a5f' : 'white',
              color:        msg.role === 'user' ? 'white' : '#111827',
              borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
              padding:      '12px 16px',
              fontSize:     14,
              lineHeight:   1.6,
              boxShadow:    '0 1px 3px rgba(0,0,0,0.08)',
              border:       msg.role === 'assistant' ? '1px solid #f0f0f0' : 'none',
              whiteSpace:   'pre-wrap',
            }}>
              {msg.content}
            </div>
          </div>
        ))}

        {/* Typing indicator */}
        {sending && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#1e3a5f', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>🛡️</div>
            <div style={{ background: 'white', borderRadius: '16px 16px 16px 4px', padding: '12px 16px', border: '1px solid #f0f0f0', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <div style={{ display: 'flex', gap: 4 }}>
                {[0,1,2].map(i => (
                  <div key={i} style={{
                    width: 8, height: 8, borderRadius: '50%', background: '#9ca3af',
                    animation: 'bounce 1.4s infinite',
                    animationDelay: `${i * 0.2}s`,
                  }} />
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 'finalising' && (
          <div style={{ textAlign: 'center', padding: '20px 0', color: '#6b7280', fontSize: 13 }}>
            ⏳ Finalising your assessment…
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      {step === 'chat' && (
        <div style={{
          background: 'white', borderTop: '1px solid #f0f0f0',
          padding: '16px 24px',
          boxShadow: '0 -1px 3px rgba(0,0,0,0.04)',
        }}>
          <div style={{ maxWidth: 720, margin: '0 auto', display: 'flex', gap: 10 }}>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your answer here… (Press Enter to send)"
              disabled={sending}
              rows={2}
              style={{
                flex: 1, padding: '10px 14px', borderRadius: 10,
                border: '1px solid #e5e7eb', fontSize: 14,
                resize: 'none', outline: 'none', fontFamily: 'inherit',
                color: '#111827', lineHeight: 1.5,
              }}
            />
            <button
              onClick={sendMessage}
              disabled={sending || !input.trim()}
              style={{
                background: sending || !input.trim() ? '#e5e7eb' : '#1e3a5f',
                color:      sending || !input.trim() ? '#9ca3af' : 'white',
                border: 'none', borderRadius: 10,
                padding: '0 20px', fontWeight: 700, fontSize: 13,
                cursor: sending || !input.trim() ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s', minWidth: 80,
              }}
            >
              {sending ? '⏳' : 'Send →'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuitabilityAssessment;